import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { useSettings } from '../../context/SettingsContext';

const MAPS = ['Bermuda', 'Purgatory', 'Kalahari', 'Nextera', 'Erangel', 'Miramar', 'Sanhok', 'অন্যান্য'];
const DEFAULT_IMAGES = {
  'FreeFire': 'banner_freefire_classic',
  'PUBG': 'banner_pubg_classic',
  'COD': '', 'Ludo': '', 'অন্যান্য': '',
};

const defaultForm = {
  title: '', game: 'FreeFire', gameMode: 'Classic Match', map: 'Bermuda',
  entryFees: { Solo: '', Duo: '', Squad: '' },
  enabledTypes: { Solo: true, Duo: true, Squad: true },
  totalPrize: '', perKillPrize: '', prizeNote: '',
  maxSlots: '20', matchTime: '',
  roomId: '', roomPassword: '', roomNote: '', banner: '', status: 'upcoming'
};

const NumInput = ({ placeholder, value, onChange }) => {
  const [local, setLocal] = useState(value === 0 || value === '' ? '' : String(value));
  useEffect(() => { setLocal(value === 0 || value === '' ? '' : String(value)); }, [value]);
  return (
    <input className="input-field" type="text" inputMode="numeric" pattern="[0-9]*"
      placeholder={placeholder} value={local}
      onChange={e => {
        const v = e.target.value.replace(/[^0-9]/g, '');
        setLocal(v);
        onChange(v === '' ? '' : Number(v));
      }}/>
  );
};

export default function AdminMatchForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [filteredModes, setFilteredModes] = useState([]);

  useEffect(() => {
    API.get('/categories/admin/all').then(res => {
      setCategories(res.data);
      if (!isEdit) {
        const games = [...new Set(res.data.map(c => c.game))];
        const firstGame = games[0] || 'FreeFire';
        const modes = res.data.filter(c => c.game === firstGame).map(c => c.name);
        setFilteredModes(modes);
      }
    }).catch(() => {});

    if (isEdit) {
      API.get(`/matches/${id}`).then(res => {
        const m = res.data;
        setForm({
          title: m.title, game: m.game, gameMode: m.gameMode || '', map: m.map || 'Bermuda',
          entryFees: m.entryFees || { Solo: '', Duo: '', Squad: '' },
          enabledTypes: m.enabledTypes || { Solo: true, Duo: true, Squad: true },
          totalPrize: m.totalPrize, perKillPrize: m.perKillPrize,
          prizeNote: m.prizeNote || '', maxSlots: m.maxSlots,
          matchTime: new Date(m.matchTime).toISOString().slice(0,16),
          roomId: m.roomId || '', roomPassword: m.roomPassword || '',
          roomNote: m.roomNote || '', banner: m.banner || '', status: m.status
        });
        if (m.banner) setBannerPreview(m.banner.startsWith('http') ? m.banner : `${(process.env.REACT_APP_API_URL||'/api').replace('/api','')}${m.banner}`);
        API.get('/categories/admin/all').then(catRes => {
          const modes = catRes.data.filter(c => c.game === m.game).map(c => c.name);
          setFilteredModes(modes.length > 0 ? modes : [m.gameMode]);
        }).catch(() => setFilteredModes([m.gameMode]));
      }).catch(() => toast.error('Match not found'));
    }
  }, [id]);

  const set = (k, v) => setForm(f => ({...f, [k]: v}));
  const setFee = (type, v) => setForm(f => ({...f, entryFees: {...f.entryFees, [type]: v}}));
  const toggleType = (type) => setForm(f => ({...f, enabledTypes: {...f.enabledTypes, [type]: !f.enabledTypes[type]}}));

  const handleSubmit = async () => {
    if (!form.title || !form.totalPrize || !form.matchTime)
      return toast.error('সব প্রয়োজনীয় তথ্য দিন');
    setLoading(true);
    try {
      const settingsKey = DEFAULT_IMAGES[form.game];
      const settingsDefaultBanner = settingsKey ? (settings[settingsKey] || '') : '';
      let bannerUrl = form.banner || settingsDefaultBanner;
      if (bannerFile) {
        setUploading(true);
        const data = new FormData();
        data.append('image', bannerFile);
        const res = await API.post('/settings/upload', data, { headers: { 'Content-Type': 'multipart/form-data' } });
        bannerUrl = res.data.url;
        setUploading(false);
      }
      const matchData = { ...form, banner: bannerUrl };
      if (isEdit) { await API.put(`/matches/${id}`, matchData); toast.success('Updated!'); }
      else { await API.post('/matches', matchData); toast.success('Match তৈরি হয়েছে!'); }
      navigate('/admin/matches');
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setLoading(false); setUploading(false); }
  };

  return (
    <AdminLayout title={isEdit ? 'Match Edit' : 'নতুন Match'}>
      <div className="admin-form">
        <div className="form-group">
          <label className="form-label">ম্যাচের নাম *</label>
          <input className="input-field" placeholder="Match এর নাম" value={form.title} onChange={e => set('title', e.target.value)}/>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Game *</label>
            <select className="input-field" value={form.game} onChange={e => {
              const game = e.target.value;
              const modes = categories.filter(c => c.game === game).map(c => c.name);
              setFilteredModes(modes);
              set('game', game);
              if (modes.length > 0) set('gameMode', modes[0]);
            }}>
              {[...new Set(categories.map(c => c.game))].map(g => <option key={g}>{g}</option>)}
              {categories.length === 0 && ['FreeFire', 'PUBG', 'Ludo', 'অন্যান্য'].map(g => <option key={g}>{g}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Game Mode</label>
            <select className="input-field" value={form.gameMode} onChange={e => set('gameMode', e.target.value)}>
              {(filteredModes.length > 0 ? filteredModes : ['Classic Match', 'Clash Squad', 'CS 1 VS 1', 'Lone Wolf']).map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Map</label>
            <select className="input-field" value={form.map} onChange={e => set('map', e.target.value)}>
              {MAPS.map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Max Slots</label>
            <NumInput placeholder="20" value={form.maxSlots} onChange={v => set('maxSlots', v)}/>
          </div>
        </div>

        {/* Entry Types with fees and toggle */}
        <div className="form-group">
          <label className="form-label">Entry Types & Fees</label>
          <div style={{display:'flex', flexDirection:'column', gap:'10px'}}>
            {['Solo', 'Duo', 'Squad'].map(type => (
              <div key={type} style={{
                background: form.enabledTypes[type] ? 'rgba(37,99,235,0.06)' : 'var(--bg-card2)',
                border: `1.5px solid ${form.enabledTypes[type] ? 'var(--border)' : 'rgba(239,68,68,0.2)'}`,
                borderRadius:10, padding:'12px',
                display:'flex', alignItems:'center', gap:'12px'
              }}>
                <label style={{display:'flex', alignItems:'center', gap:'8px', cursor:'pointer', minWidth:80}}>
                  <input type="checkbox" checked={form.enabledTypes[type]}
                    onChange={() => toggleType(type)}
                    style={{width:16, height:16, cursor:'pointer'}}/>
                  <span style={{fontWeight:700, fontSize:'14px', color: form.enabledTypes[type] ? 'var(--text-primary)' : 'var(--text-muted)'}}>
                    {type === 'Solo' ? '👤 Solo' : type === 'Duo' ? '👥 Duo' : '👨‍👩‍👧‍👦 Squad'}
                  </span>
                </label>
                <div style={{flex:1}}>
                  <NumInput
                    placeholder={`${type} fee (৳)`}
                    value={form.entryFees[type]}
                    onChange={v => setFee(type, v)}
                  />
                </div>
                {!form.enabledTypes[type] && (
                  <span style={{fontSize:'11px', color:'var(--danger)', fontWeight:600}}>বন্ধ</span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Total Prize (৳) *</label>
            <NumInput placeholder="500" value={form.totalPrize} onChange={v => set('totalPrize', v)}/>
          </div>
          <div className="form-group">
            <label className="form-label">Per Kill Prize (৳)</label>
            <NumInput placeholder="0" value={form.perKillPrize} onChange={v => set('perKillPrize', v)}/>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Prize Note</label>
          <textarea className="input-field" rows={2} placeholder="e.g. 1st: 100৳, 2nd: 50৳" value={form.prizeNote} onChange={e => set('prizeNote', e.target.value)}/>
        </div>

        <div className="form-group">
          <label className="form-label">Match Time *</label>
          <input className="input-field" type="datetime-local" value={form.matchTime} onChange={e => set('matchTime', e.target.value)}/>
        </div>

        {/* Banner */}
        <div className="form-group">
          <label className="form-label">🖼 Match Banner</label>
          <label style={{display:'block', cursor:'pointer'}}>
            <div style={{border:'2px dashed var(--border)', borderRadius:10, overflow:'hidden', background:'var(--bg-card2)', position:'relative'}}>
              {(() => {
                const settingsKey = DEFAULT_IMAGES[form.game];
                const defaultImg = settingsKey ? (settings[settingsKey] || '') : '';
                const showImg = bannerPreview || (defaultImg?.startsWith('http') ? defaultImg : '');
                return showImg ? (
                  <img src={showImg} alt="banner" style={{width:'100%', height:'120px', objectFit:'cover', display:'block'}} onError={e => { e.target.style.display='none'; }}/>
                ) : (
                  <div style={{height:'80px', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:'6px'}}>
                    <span style={{fontSize:'24px'}}>🖼</span>
                    <span style={{fontSize:'12px', color:'var(--text-muted)'}}>Banner আপলোড করুন</span>
                  </div>
                );
              })()}
              <div style={{padding:'6px', background:'rgba(0,0,0,0.5)', position:'absolute', bottom:0, left:0, right:0, fontSize:'11px', color:'#ccc', textAlign:'center'}}>
                📷 ক্লিক করে image দিন
              </div>
            </div>
            <input type="file" accept="image/*" onChange={e => { const f=e.target.files[0]; if(f){setBannerFile(f);setBannerPreview(URL.createObjectURL(f));} }} style={{display:'none'}}/>
          </label>
        </div>

        {/* Room Details */}
        <div style={{background:'var(--bg-card2)', borderRadius:10, padding:'14px'}}>
          <p style={{fontSize:'13px', color:'var(--purple-light)', fontWeight:600, marginBottom:'10px'}}>🔑 Room Details</p>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Room ID</label>
              <input className="input-field" value={form.roomId} onChange={e => set('roomId', e.target.value)}/>
            </div>
            <div className="form-group">
              <label className="form-label">Room Password</label>
              <input className="input-field" value={form.roomPassword} onChange={e => set('roomPassword', e.target.value)}/>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Room Note</label>
            <input className="input-field" value={form.roomNote} onChange={e => set('roomNote', e.target.value)}/>
          </div>
        </div>

        {isEdit && (
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="input-field" value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="upcoming">upcoming</option>
              <option value="running">running</option>
              <option value="completed">completed</option>
              <option value="cancelled">cancelled</option>
            </select>
          </div>
        )}

        <button className="btn-primary" onClick={handleSubmit} disabled={loading || uploading}>
          {uploading ? '⏳ Image আপলোড হচ্ছে...' : loading ? '⏳ সেভ হচ্ছে...' : isEdit ? '✅ আপডেট করুন' : '✅ Match তৈরি করুন'}
        </button>
      </div>
    </AdminLayout>
  );
}
